// QRScanner — camera-based QR decoder for the gate kiosk.
//
// Uses the native BarcodeDetector API (Chrome/Edge/Brave + iOS Safari 17+).
// When the device's browser doesn't expose it, the component renders a
// "scanner unavailable" notice so the operator falls back to typed entry.
// Zero npm dependencies; the detector is in the browser.
//
// The QR sheet generator (services/api/internal/identity/qrsheet.go)
// encodes payloads of the shape:
//
//   <studentUUID>|<schoolUUID>|<base64-hmac-sig>
//
// The gate device is already a trusted bearer (its api_token authenticates
// the /arrivals POST), so we only need the studentUUID from the payload -
// the server doesn't separately verify the QR signature on the gate path.
// We surface the parsed UUID through the onScan callback.

import { useEffect, useRef, useState } from "react";

import { Button } from "@sms/design-system";

type DetectedBarcode = { rawValue: string };
type BarcodeDetectorCtor = new (init?: { formats: string[] }) => {
  detect: (source: CanvasImageSource | HTMLVideoElement) => Promise<DetectedBarcode[]>;
};

declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorCtor;
  }
}

export interface QRScannerProps {
  /**
   * Called with the raw QR string once per successful decode. The caller
   * is responsible for parsing/validating + submitting; this component
   * just decodes and surfaces.
   */
  onScan: (rawValue: string) => void;
  /** Operator closed the scanner (e.g. to switch back to typed entry). */
  onClose: () => void;
  /** Localised labels passed in to keep the i18n boundary in the parent. */
  labels: {
    closeAction: string;
    requestingCamera: string;
    permissionDenied: string;
    unsupported: string;
  };
}

export function QRScanner({ onScan, onClose, labels }: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cancelledRef = useRef(false);
  const [error, setError] = useState<"denied" | "unsupported" | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    cancelledRef.current = false;
    const Detector = window.BarcodeDetector;
    if (!Detector) {
      setError("unsupported");
      return;
    }
    const detector = new Detector({ formats: ["qr_code"] });

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelledRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        setReady(true);
        const loop = async () => {
          if (cancelledRef.current || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0 && codes[0]?.rawValue) {
              onScan(codes[0].rawValue);
              return; // stop the loop; parent will unmount or recall scan
            }
          } catch {
            /* transient detect errors are fine — keep looping */
          }
          requestAnimationFrame(() => void loop());
        };
        void loop();
      } catch {
        setError("denied");
      }
    };
    void start();

    return () => {
      cancelledRef.current = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [onScan]);

  if (error === "unsupported") {
    return (
      <div role="alert" style={messageStyle}>
        <p>{labels.unsupported}</p>
        <Button variant="secondary" onClick={onClose}>
          {labels.closeAction}
        </Button>
      </div>
    );
  }
  if (error === "denied") {
    return (
      <div role="alert" style={messageStyle}>
        <p>{labels.permissionDenied}</p>
        <Button variant="secondary" onClick={onClose}>
          {labels.closeAction}
        </Button>
      </div>
    );
  }

  return (
    <div style={scannerStyle} data-testid="qr-scanner">
      <video
        ref={videoRef}
        playsInline
        muted
        style={videoStyle}
        aria-label="QR scanner viewfinder"
      />
      {!ready && (
        <p role="status" style={statusStyle}>
          {labels.requestingCamera}
        </p>
      )}
      <div style={{ marginTop: 8 }}>
        <Button variant="secondary" onClick={onClose}>
          {labels.closeAction}
        </Button>
      </div>
    </div>
  );
}

const scannerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  marginTop: 12,
};
const videoStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 360,
  aspectRatio: "1 / 1",
  borderRadius: 12,
  background: "#0f172a",
  objectFit: "cover",
};
const statusStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#475569",
};
const messageStyle: React.CSSProperties = {
  padding: 16,
  background: "#fef2f2",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  color: "#991b1b",
  fontSize: 14,
};

// Helper exported for the kiosk to extract a studentUUID from the raw QR.
// The signed payload is `studentUUID.schoolUUID.base64sig` (dot separator;
// see services/api/internal/identity/qrsheet.go qrPayloadSep). If only a
// bare UUID is encoded (e.g. an old-format QR), return it unchanged.
//
// Picking the first '.'-delimited segment is safe because UUIDs use '-'
// and never contain '.', and the base64-RAW-URL alphabet (A-Za-z0-9_-)
// has no '.' either.
export function parseStudentIdFromQR(raw: string): string {
  const head = raw.split(".", 1)[0]?.trim() ?? "";
  return head;
}
